import { defineComponent as _defineComponent } from 'vue'
import Modal from "../Modal.vue";
  import {
    computed,
    reactive,
    ref,
    watchEffect,
    onMounted,
  } from "vue";
  import CheckboxInput from "../input/CheckboxInput.vue";
  import useVuelidate from "@vuelidate/core";
  import { required, email, helpers, not, sameAs } from "@vuelidate/validators";
  import Cleave from "vue-cleave-component";
  import { phoneNumberOption, phonePlaceholder } from "@/helpers/cleavePlugin";
  import { validPhoneNumber } from "@/helpers/validation";
  import { resetVuelidateErrors } from "@/helpers/vuelidate";
  import store from "@/store";
  import LeadSourceEnum from "@/enums/LeadSourceEnum";
  import { POSITION, useToast } from "vue-toastification";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import { formatPrice } from "@/helpers/transformation/price";
  import HouseFeatures from "@/components/builder/HouseFeatures.vue";
  import { FacadeInterface } from "@/types/FacadeInterface";
  import { BuilderQuote, UpdateBuilderQuoteDto } from "@/types/BuilderQuote";
  import { calculateBuildPriceOrPackagePriceTotal } from "@/helpers/builder/calculateBuildPriceOrPackagePriceTotal";
  import KlaviyoErrorEnum from "@/enums/KlaviyoEnum";
  import axios, { AxiosError } from "axios";
  import parseAustralianNumberToInternational from "@/helpers/phone-number";
  import ErrorMessages from "@/constants/errorsMessages";
  import { handleError } from "@/helpers/klaviyo/klaviyo-error-handler.helper";
  import { FinanceStatusEnum } from "@/enums/BuilderFinanceStatus";
  import scrollToBottomModal from "@/helpers/scroll-modal.helper";
  import { HouseDesignInterface } from "@/types/HouseDesignInterface";
  import { LotInterface } from "@/types/LotInterface";
  import { EstateInterface } from "@/types/EstateInterface";
  import { BuilderInterface } from "@/types/BuilderInterface";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'BuilderEnquiryModal',
  props: {
    isOpen: { type: Boolean },
    house: {},
    lot: {},
    quote: {},
    estate: {},
    builder: {},
    selectedFacade: {}
  },
  emits: ["close", "send"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const toast = useToast();

  const emits = __emit;

  const props = __props;

  const optionsFinanceStatus = [
    FinanceStatusEnum.PRE_APPROVED,
    FinanceStatusEnum.APPLICATION_IN_PROGRESS,
    FinanceStatusEnum.EXPLORING_MY_OPTIONS,
    FinanceStatusEnum.CASH_BUYER,
  ];

  const rules = {
    firstName: { required },
    lastName: { required },
    email: { required, email },
    phone: {
      required,
      validPhoneNumber: helpers.withMessage(
        "Value must be valid phone number",
        validPhoneNumber
      ),
    },
    message: { optional: true },
    preferredLots: { optional: true },
    financeStatus: { optional: true },
  };

  const formFields = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
    preferredLots: "",
    financeStatus: FinanceStatusEnum.SELECT,
  };
  const form = reactive(Object.assign({}, formFields));
  const v$ = useVuelidate(rules, form);
  const isLoading = ref(false);
  const facade = computed<FacadeInterface>(() => props.selectedFacade);
  const siteCosts = ref<number>(0);
  const modalBodyRef = ref<HTMLElement | null>(null);
  const selectedFacadeId = computed(
    () => store.getters["facade/selectedFacadeId"]
  );

  const getSiteCosts = () => {
    if (props.quote) {
      store
        .dispatch("builder/getSiteCost", {
          lotId: props.quote.selections.lot.lotId,
          builderId: props.quote.selections.builder.builderId,
          houseId: props.quote.selections.house.houseId,
        })
        .then((res) => {
          siteCosts.value = res.data.siteCost;
        })
        .catch(() => {
          toast.error("Fetching site costs failed", {
            position: POSITION.TOP_LEFT,
          });
        });
    }
  };

  const buildPriceWithFacade = computed(() => {
    const facade = props.house.facades.find(
      (facade) => facade._id === selectedFacadeId.value
    );
    const basePrice = Number(props.house.basePrice);
    const facadePrice = Number(facade?.price || 0);

    return calculateBuildPriceOrPackagePriceTotal(
      basePrice,
      facadePrice,
      siteCosts.value
    );
  });

  const onClose = () => {
    clearForm();
    resetVuelidateErrors(v$);
    emits("close");
  };

  const submitEnquiry = async () => {
    const isValid = await validateForm();

    if (!isValid) {
      return;
    }

    try {
      isLoading.value = true;

      const body = createBodyRequest();

      await store.dispatch("builder/enquiryBuilderPackage", body);

      emits("send");
      onClose();
    } catch (error) {
      toast.error("Something went wrong, please try again later", {
        position: POSITION.TOP_LEFT,
      });
    } finally {
      isLoading.value = false;
    }
  };

  const createBodyRequest = () => {
    const { lot } = props;
    const { landownerId } = lot;

    const selections = {
      builder: {
        builderId: props.builder._id,
        name: props.builder.name,
        logoUrl: props.builder.logo,
      },
      house: {
        houseId: props.house._id,
        name: props.house.name,
        price: props.house.basePrice,
      },
      facade: {
        facadeId: facade.value?._id as string,
        name: facade.value?.name as string,
        url: facade.value?.url as string,
        price: facade.value?.price as number,
      },
      floorplan: {
        name: "Floorplan",
        url: props.house.floorplans[0].url,
      },
      lot: {
        lotId: lot._id,
        name: lot.title,
        price: lot.price,
      },
    };

    const financeStatus =
      form.financeStatus === FinanceStatusEnum.SELECT
        ? undefined
        : form.financeStatus;
    const preferredLots =
      form.preferredLots === "" ? undefined : form.preferredLots;
    const message = form.message === "" ? undefined : form.message;

    return {
      selections,
      estateTitle: props.estate.title,
      message,
      preferredLots,
      financeStatus,
      lead: {
        landownerId: lot.landownerId,
        estateId: lot.estateId._id,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email.toLowerCase(),
        phone: form.phone,
        source: LeadSourceEnum.ENQUIRY,
        watched: false,
      },
    };
  };

  const validateForm = async () => {
    const isValid = await v$.value.$validate();

    v$.value.$touch();
    return isValid;
  };

  const clearForm = () => {
    Object.assign(form, formFields);
    resetVuelidateErrors(v$);
  };

  const onFinanceStatusDropdownClick = async () => {
    await scrollToBottomModal(modalBodyRef, modalBodyRef);
  };

  const selectFinanceStatus = (financeStatus: FinanceStatusEnum) => {
    form.financeStatus = financeStatus;
  };

  watchEffect(() => {
    if (props.isOpen) {
      getSiteCosts();
    }
  });

  onMounted(() => {
    getSiteCosts();
  });

const __returned__ = { toast, emits, props, optionsFinanceStatus, rules, formFields, form, v$, isLoading, facade, siteCosts, modalBodyRef, selectedFacadeId, getSiteCosts, buildPriceWithFacade, onClose, submitEnquiry, createBodyRequest, validateForm, clearForm, onFinanceStatusDropdownClick, selectFinanceStatus, Modal, computed, reactive, ref, watchEffect, onMounted, CheckboxInput, get useVuelidate() { return useVuelidate }, get required() { return required }, get email() { return email }, get helpers() { return helpers }, get not() { return not }, get sameAs() { return sameAs }, get Cleave() { return Cleave }, get phoneNumberOption() { return phoneNumberOption }, get phonePlaceholder() { return phonePlaceholder }, get validPhoneNumber() { return validPhoneNumber }, get resetVuelidateErrors() { return resetVuelidateErrors }, get store() { return store }, get LeadSourceEnum() { return LeadSourceEnum }, get POSITION() { return POSITION }, get useToast() { return useToast }, DropDown, get formatPrice() { return formatPrice }, HouseFeatures, get FacadeInterface() { return FacadeInterface }, get BuilderQuote() { return BuilderQuote }, get UpdateBuilderQuoteDto() { return UpdateBuilderQuoteDto }, get calculateBuildPriceOrPackagePriceTotal() { return calculateBuildPriceOrPackagePriceTotal }, get KlaviyoErrorEnum() { return KlaviyoErrorEnum }, get axios() { return axios }, get AxiosError() { return AxiosError }, get parseAustralianNumberToInternational() { return parseAustralianNumberToInternational }, get ErrorMessages() { return ErrorMessages }, get handleError() { return handleError }, get FinanceStatusEnum() { return FinanceStatusEnum }, get scrollToBottomModal() { return scrollToBottomModal }, get HouseDesignInterface() { return HouseDesignInterface }, get LotInterface() { return LotInterface }, get EstateInterface() { return EstateInterface }, get BuilderInterface() { return BuilderInterface } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})